import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useMutation } from '@tanstack/react-query';
import * as Haptics from 'expo-haptics';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  Building2,
  CalendarClock,
  CreditCard,
  Download,
  EyeOff,
  KeyRound,
  RotateCcw,
  Trash2,
  UserCheck,
  AlertTriangle,
} from 'lucide-react-native';
import { AppLayout, type Breadcrumb } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { AccountActionButton } from '@/components/account/AccountActionButton';
import { StepUpModal } from '@/components/account/StepUpModal';
import { CompanyOutcomePicker } from '@/components/account/CompanyOutcomePicker';
import { useAlert } from '@/components/AlertDialog';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase';
import { handleError } from '@/lib/errors';
import { track } from '@/lib/analytics';
import { isDemoMode } from '@/lib/config/app-mode';
import {
  AccountDeletionError,
  PartialRequestError,
  StepUpRequiredError,
  buildCompanyOutcomes,
  deletionBackendAvailable,
  formatDeletionDate,
  isDeleteConfirmation,
  purgeLocalSession,
  useDeletionPreview,
  useRequestDeletion,
  type CompanyOutcomeSelection,
  type DeletionPreview,
  type DeletionPreviewCompany,
} from '@/lib/gdpr/account-deletion';
import {
  DataExportError,
  deliverExport,
  isStillDeliverable,
  requestDataExport,
  type DataExport,
} from '@/lib/gdpr/deliver-export';

/** What the step-up, once passed, should retry. */
type PendingAction = 'preview' | 'request' | 'export';

const MAX_WIDTH = 640;

/**
 * Delete account — App Store 5.1.1(v) / GDPR Art. 17.
 *
 * One screen, three moments: what happens (from the SERVER's preview, so the
 * co-founder named as the new owner is the one the erasure will actually pick),
 * a typed confirmation, and the MFA step-up when the server asks for it. The
 * request SCHEDULES deletion 30 days out; a partial result is never shown as
 * done (data-retention-policy :88).
 */
export default function DeleteAccountScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, signOut } = useAuth();
  const { alert } = useAlert();

  const available = deletionBackendAvailable() && !isDemoMode();
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState<string>('');
  const [partial, setPartial] = useState<PartialRequestError | null>(null);
  const [stepUpFor, setStepUpFor] = useState<PendingAction | null>(null);
  // 119-07: the last founder's choice per company (startup id → selection).
  const [outcomes, setOutcomes] = useState<Record<string, CompanyOutcomeSelection>>({});
  const [outcomeProblems, setOutcomeProblems] = useState<Record<string, string>>({});

  const preview = useDeletionPreview(available && !!user && !scheduledFor);
  const request = useRequestDeletion();
  const lastExport = useRef<DataExport | null>(null);

  // The preview is behind AAL2: an enrolled AAL1 session gets the step-up
  // prompt straight away rather than an error card.
  useEffect(() => {
    if (preview.error instanceof StepUpRequiredError) setStepUpFor('preview');
  }, [preview.error]);

  const exportData = useMutation({
    mutationFn: async () => {
      const reused = isStillDeliverable(lastExport.current);
      const result = reused && lastExport.current ? lastExport.current : await requestDataExport();
      lastExport.current = result;
      return deliverExport(result);
    },
    onSuccess: (outcome) => {
      if (outcome.status === 'failed') alert('Export Not Saved', outcome.message);
      else if (outcome.status === 'delivered') {
        alert('Export Ready', isWeb ? 'Your download has started.' : 'Your export was handed to the share sheet.');
      }
    },
    onError: (err) => {
      if (err instanceof DataExportError && err.kind === 'step_up_required') {
        setStepUpFor('export');
        return;
      }
      const { message, isCancelled } = handleError(err, { context: 'DeleteAccount.export' });
      if (!isCancelled) alert('Export Failed', err instanceof DataExportError ? err.userMessage : message);
    },
  });

  const chooseOutcome = (startupId: string, next: CompanyOutcomeSelection) => {
    setOutcomes((prev) => ({ ...prev, [startupId]: next }));
    setOutcomeProblems((prev) => {
      if (!(startupId in prev)) return prev;
      const { [startupId]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const submit = () => {
    if (!isDeleteConfirmation(confirmText) || request.isPending || !preview.data) return;
    // Every company you are the last member of gets an explicit choice (the
    // default is closed); an incomplete one (acquired, no acquirer) stops here.
    const built = buildCompanyOutcomes(preview.data.companies, outcomes);
    if (!built.ok) {
      setOutcomeProblems(built.problems);
      return;
    }
    setOutcomeProblems({});
    if (!isWeb) Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
    setPartial(null);
    request.mutate(built.body, {
      onSuccess: async (r) => {
        // The existing event: it now marks the scheduled request (the erasure itself
        // runs server-side 30 days later, with nobody signed in to track it).
        track('account_deleted');
        setScheduledFor(r.scheduledFor);
        // The server ended every session; follow it locally, completely.
        await purgeLocalSession(signOut);
      },
      onError: (err) => {
        if (err instanceof StepUpRequiredError) {
          setStepUpFor('request');
          return;
        }
        if (err instanceof PartialRequestError) {
          setPartial(err);
          return;
        }
        const { message, isCancelled } = handleError(err, { context: 'DeleteAccount.request' });
        if (isCancelled) return;
        alert('Deletion Not Scheduled', err instanceof AccountDeletionError ? err.userMessage : message);
      },
    });
  };

  const onStepUpVerified = () => {
    const action = stepUpFor;
    setStepUpFor(null);
    if (action === 'preview') preview.refetch();
    else if (action === 'request') submit();
    else if (action === 'export') exportData.mutate();
  };

  const leave = () => {
    if (isWeb && typeof window !== 'undefined') window.location.replace('/');
    else router.replace('/');
  };

  const breadcrumbs: Breadcrumb[] = [{ label: 'Settings', href: '/settings' }, { label: 'Delete account' }];

  return (
    <AppLayout title="Delete account" breadcrumbs={breadcrumbs}>
      <ScreenFrame
        title="Delete account"
        back={scheduledFor ? undefined : { fallback: '/settings', label: 'Settings' }}
        bottomEdge
      >
        <KeyboardAwareBody
          contentContainerStyle={{ paddingHorizontal: 20, paddingTop: 8, paddingBottom: 48, flexGrow: 1 }}
        >
          <View style={{ width: '100%', maxWidth: MAX_WIDTH, alignSelf: 'center', gap: 20 }}>
            {scheduledFor ? (
              <ScheduledPanel scheduledFor={scheduledFor} onDone={leave} />
            ) : !available ? (
              <Notice
                title="Not available here"
                body={
                  isDemoMode()
                    ? 'Shared demo accounts are used by everyone who tries Inverio, so they cannot be deleted. Sign in with your own account to delete it.'
                    : 'Account deletion needs a live account. It is not available in offline sample mode.'
                }
              />
            ) : (
              <>
                <Animated.View entering={FadeInDown.duration(350)}>
                  <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 28, fontWeight: '700', letterSpacing: -0.5 }}>
                    What happens
                  </Text>
                  <Text numberOfLines={4} style={{ color: colors.text.secondary, fontSize: 15, lineHeight: 22, marginTop: 6 }}>
                    Your account is scheduled for deletion, not deleted on the spot. Here is exactly what changes, and when.
                  </Text>
                </Animated.View>

                {preview.isLoading ? (
                  <View className="items-center justify-center" style={{ paddingVertical: 48 }}>
                    <ActivityIndicator color={colors.primary[600]} />
                  </View>
                ) : preview.data ? (
                  <>
                    <ConsequenceLedger preview={preview.data} />
                    {preview.data.companies
                      .filter((c) => c.action === 'unclaimed' && c.choices.length > 0)
                      .map((c) => (
                        <CompanyOutcomePicker
                          key={`outcome-${c.startupId}`}
                          company={c}
                          value={outcomes[c.startupId]}
                          onChange={(next) => chooseOutcome(c.startupId, next)}
                          problem={outcomeProblems[c.startupId] ?? null}
                        />
                      ))}
                  </>
                ) : preview.error instanceof StepUpRequiredError ? (
                  <Notice
                    title="Confirm it's you first"
                    body="Deleting an account needs your authenticator code. Verify to see what deleting will do."
                    action={{ label: 'Enter my code', onPress: () => setStepUpFor('preview') }}
                  />
                ) : preview.error ? (
                  <Notice
                    title="Couldn't load the details"
                    body={
                      preview.error instanceof AccountDeletionError
                        ? preview.error.userMessage
                        : 'Check your connection and try again.'
                    }
                    action={
                      preview.error instanceof AccountDeletionError && preview.error.kind === 'demo_account'
                        ? undefined
                        : { label: 'Try again', onPress: () => preview.refetch() }
                    }
                  />
                ) : null}

                {preview.data && (
                  <>
                    <AccountActionButton
                      variant="secondary"
                      label={exportData.isPending ? 'Preparing your data…' : 'Download my data first'}
                      loading={exportData.isPending}
                      icon={<Download size={18} color={colors.text.primary} />}
                      onPress={() => {
                        if (!exportData.isPending) exportData.mutate();
                      }}
                    />

                    {partial && (
                      <Animated.View
                        entering={FadeInDown.duration(250)}
                        style={{
                          borderRadius: 16,
                          padding: 16,
                          gap: 12,
                          backgroundColor: colors.amber.light,
                          borderWidth: 1,
                          borderColor: colors.amber.muted,
                        }}
                      >
                        <View className="flex-row items-start" style={{ gap: 10 }}>
                          <View className="shrink-0" style={{ paddingTop: 2 }}>
                            <AlertTriangle size={18} color={colors.amber.main} />
                          </View>
                          <Text numberOfLines={4} className="flex-1" style={{ color: colors.text.primary, fontSize: 14, lineHeight: 20 }}>
                            {partial.userMessage}
                          </Text>
                        </View>
                        <AccountActionButton
                          variant="secondary"
                          label="Retry"
                          icon={<RotateCcw size={18} color={colors.text.primary} />}
                          loading={request.isPending}
                          onPress={submit}
                        />
                      </Animated.View>
                    )}

                    <ConfirmBlock
                      value={confirmText}
                      onChange={setConfirmText}
                      onSubmit={submit}
                      pending={request.isPending}
                    />
                  </>
                )}
              </>
            )}
          </View>
        </KeyboardAwareBody>
      </ScreenFrame>

      <StepUpModal
        visible={stepUpFor !== null}
        reason={stepUpFor === 'export' ? 'download your data' : 'delete your account'}
        onClose={() => setStepUpFor(null)}
        onVerified={onStepUpVerified}
      />
    </AppLayout>
  );
}

// -----------------------------------------------------------------------------
// Pieces
// -----------------------------------------------------------------------------

function companyLine(c: DeletionPreviewCompany): { title: string; detail: string } {
  const name = c.name ?? 'Your company';
  switch (c.action) {
    case 'transferred':
      return {
        title: `${name} → ${c.newOwnerName ?? 'a co-founder'} becomes owner`,
        detail: 'The company and its data stay with the team. Your access ends.',
      };
    case 'unclaimed':
      // 119-06: the last founder's company is CLOSED with the outcome chosen
      // below, never deleted outright; its data is sealed unless they choose
      // to delete it. (The 118 copy — "your documents, decks, interviews and
      // scores are deleted" — stopped being true.)
      return {
        title: `${name} → closes with the outcome you choose`,
        detail: 'It stays listed with its status and score history. Its data is sealed, not deleted, unless you choose below to delete it.',
      };
    default:
      return {
        title: `${name} → you leave the team`,
        detail: 'The others keep running it. Your membership is removed.',
      };
  }
}

function ConsequenceLedger({ preview }: { preview: DeletionPreview }) {
  const colors = useThemeColors();
  const date = formatDeletionDate(preview.scheduledForIfRequested);
  const rows: { key: string; icon: React.ReactNode; title: string; detail: string; tone?: 'now' }[] = [];

  preview.companies.forEach((c, i) => {
    const line = companyLine(c);
    rows.push({
      key: `company-${i}`,
      icon:
        c.action === 'transferred' ? (
          <UserCheck size={18} color={colors.primary[600]} />
        ) : (
          <Building2 size={18} color={colors.text.secondary} />
        ),
      ...line,
    });
  });
  if (preview.subscription) {
    rows.push({
      key: 'subscription',
      icon: <CreditCard size={18} color={colors.text.secondary} />,
      title: "Your subscription won't renew",
      detail: 'It is cancelled when the deletion runs. Payment records are kept as the law requires.',
    });
  }
  const creds = preview.apiKeys + preview.oauthClients;
  rows.push({
    key: 'credentials',
    icon: <KeyRound size={18} color={colors.error} />,
    title: 'API keys and connected apps are revoked now',
    detail:
      creds > 0
        ? `${creds} credential${creds === 1 ? '' : 's'} stop working immediately and are not restored if you cancel.`
        : 'Anything connected to your account stops working immediately.',
    tone: 'now',
  });
  rows.push({
    key: 'discovery',
    icon: <EyeOff size={18} color={colors.error} />,
    title: 'You are hidden from discovery now',
    detail: 'Investors and founders can no longer find or message you.',
    tone: 'now',
  });

  return (
    <Animated.View entering={FadeInDown.delay(80).duration(350)} style={{ gap: 16 }}>
      {/* The window — the one thing to remember. */}
      <View
        style={{
          borderRadius: 20,
          padding: 20,
          backgroundColor: colors.primary[50],
          borderWidth: 1,
          borderColor: colors.primary[100],
          overflow: 'hidden',
        }}
      >
        <View className="flex-row items-center" style={{ gap: 14 }}>
          <View
            className="shrink-0 items-center justify-center"
            style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: colors.surface.primary }}
          >
            <CalendarClock size={24} color={colors.primary[700]} />
          </View>
          <View className="flex-1">
            <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 17, fontWeight: '700' }}>
              You have 30 days to change your mind
            </Text>
            <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20, marginTop: 2 }}>
              Deletion runs on {date}. Until then, sign in and tap Cancel deletion.
            </Text>
          </View>
        </View>
      </View>

      <View
        style={{
          borderRadius: 20,
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          overflow: 'hidden',
        }}
      >
        {rows.map((r, i) => (
          <View
            key={r.key}
            className="flex-row items-start"
            style={{
              gap: 14,
              paddingHorizontal: 16,
              paddingVertical: 14,
              borderTopWidth: i === 0 ? 0 : 1,
              borderTopColor: colors.border.light,
            }}
          >
            <View
              className="shrink-0 items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                backgroundColor: r.tone === 'now' ? colors.red.light : colors.surface.secondary,
              }}
            >
              {r.icon}
            </View>
            <View className="flex-1">
              <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
                {r.title}
              </Text>
              <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 19, marginTop: 2 }}>
                {r.detail}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </Animated.View>
  );
}

function ConfirmBlock({
  value,
  onChange,
  onSubmit,
  pending,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  pending: boolean;
}) {
  const colors = useThemeColors();
  const matches = isDeleteConfirmation(value);
  return (
    <Animated.View
      entering={FadeInDown.delay(160).duration(350)}
      style={{
        borderRadius: 20,
        padding: 20,
        gap: 14,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        // A correctly typed DELETE is VALID, not an error: red is reserved for
        // the field that needs fixing (119-07 UAT — the box turned red beside a
        // missing acquirer name and read as a second problem).
        borderColor: matches ? colors.primary[600] : colors.border.light,
      }}
    >
      <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 17, fontWeight: '700' }}>
        Confirm
      </Text>
      <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20 }}>
        Type DELETE in capitals to schedule the deletion of your account.
      </Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        onSubmitEditing={onSubmit}
        autoCapitalize="characters"
        autoCorrect={false}
        autoComplete="off"
        spellCheck={false}
        returnKeyType="done"
        accessibilityLabel="Type DELETE to confirm"
        placeholder="DELETE"
        placeholderTextColor={colors.text.tertiary}
        style={{
          height: 52,
          borderRadius: 14,
          borderWidth: 1,
          borderColor: matches ? colors.primary[600] : colors.border.medium,
          backgroundColor: colors.surface.secondary,
          color: colors.text.primary,
          paddingHorizontal: 16,
          fontSize: 18,
          fontWeight: '600',
          letterSpacing: 4,
        }}
      />
      <AccountActionButton
        variant="destructive"
        label={pending ? 'Scheduling…' : 'Delete my account'}
        icon={<Trash2 size={18} color={colors.text.inverse} />}
        disabled={!matches}
        loading={pending}
        onPress={onSubmit}
        accessibilityHint="Schedules your account for deletion in 30 days"
      />
    </Animated.View>
  );
}

function ScheduledPanel({ scheduledFor, onDone }: { scheduledFor: string; onDone: () => void }) {
  const colors = useThemeColors();
  return (
    <Animated.View entering={FadeInDown.duration(400)} style={{ gap: 20, paddingTop: 16 }}>
      <View
        className="items-center justify-center"
        style={{ width: 64, height: 64, borderRadius: 20, backgroundColor: colors.red.light }}
      >
        <CalendarClock size={30} color={colors.error} />
      </View>
      <Text numberOfLines={3} style={{ color: colors.text.primary, fontSize: 28, fontWeight: '700', letterSpacing: -0.5 }}>
        Deletion scheduled for {formatDeletionDate(scheduledFor)}
      </Text>
      <Text numberOfLines={6} style={{ color: colors.text.secondary, fontSize: 15, lineHeight: 22 }}>
        You have been signed out on every device, and we have emailed you a confirmation. Changed your mind? Sign in
        before that date and tap Cancel deletion.
      </Text>
      <AccountActionButton label="Done" variant="primary" onPress={onDone} />
    </Animated.View>
  );
}

function Notice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; onPress: () => void };
}) {
  const colors = useThemeColors();
  return (
    <View
      style={{
        borderRadius: 20,
        padding: 20,
        gap: 12,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 17, fontWeight: '700' }}>
        {title}
      </Text>
      <Text numberOfLines={5} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20 }}>
        {body}
      </Text>
      {action && <AccountActionButton variant="secondary" label={action.label} onPress={action.onPress} />}
    </View>
  );
}
